import { supabase } from './supabase';

export interface Role {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  system: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserProfile {
  id: string;
  user_id: string;
  full_name: string | null;
  phone: string | null;
  designation: string | null;
  blood_group: string | null;
  gender: 'male' | 'female' | 'other' | null;
  address: string | null;
  signature_url: string | null;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

export interface UserWithProfile {
  id: string;
  email: string;
  user_profile: UserProfile | null;
  user_roles: Array<{ role: Role }>;
  created_at: string;
}

export interface CreateUserData {
  email: string;
  password: string;
  full_name: string;
  phone?: string;
  designation?: string;
  blood_group?: string;
  gender?: 'male' | 'female' | 'other';
  address?: string;
  status?: 'active' | 'inactive';
  role_id: string;
}

export interface UpdateUserData {
  full_name?: string;
  phone?: string;
  designation?: string;
  blood_group?: string;
  gender?: 'male' | 'female' | 'other';
  address?: string;
  signature_url?: string;
  status?: 'active' | 'inactive';
  role_id?: string;
}

export const rolesService = {
  async getAll(): Promise<Role[]> {
    const { data, error } = await supabase
      .from('roles')
      .select('*')
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<Role | null> {
    const { data, error } = await supabase
      .from('roles')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(role: Omit<Role, 'id' | 'created_at' | 'updated_at'>): Promise<Role> {
    const { data, error } = await supabase
      .from('roles')
      .insert([role])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, updates: Partial<Omit<Role, 'id' | 'created_at' | 'updated_at'>>): Promise<Role> {
    const { data, error } = await supabase
      .from('roles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('roles')
      .delete()
      .eq('id', id)
      .eq('system', false);

    if (error) throw error;
  },
};

export const usersService = {
  async getAll(): Promise<UserWithProfile[]> {
    const { data, error } = await supabase
      .from('auth.users')
      .select(`
        id,
        email,
        created_at,
        user_profile:user_profiles(*),
        user_roles(role:roles(*))
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching users:', error);
      return [];
    }

    return data || [];
  },

  async getById(userId: string): Promise<UserWithProfile | null> {
    const { data, error } = await supabase
      .from('auth.users')
      .select(`
        id,
        email,
        created_at,
        user_profile:user_profiles(*),
        user_roles(role:roles(*))
      `)
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching user:', error);
      return null;
    }

    return data;
  },

  async create(userData: CreateUserData): Promise<UserWithProfile> {
    const { data: authData, error: authError } = await supabase.auth.admin.createUser({
      email: userData.email,
      password: userData.password,
      email_confirm: true,
    });

    if (authError) throw authError;
    if (!authData.user) throw new Error('Failed to create user');

    const { error: profileError } = await supabase.rpc('update_user_profile', {
      p_user_id: authData.user.id,
      p_full_name: userData.full_name,
      p_phone: userData.phone || null,
      p_designation: userData.designation || null,
      p_blood_group: userData.blood_group || null,
      p_gender: userData.gender || null,
      p_address: userData.address || null,
      p_signature_url: null,
      p_status: userData.status || 'active',
    });

    if (profileError) throw profileError;

    const { error: roleError } = await supabase.rpc('assign_user_role', {
      p_user_id: authData.user.id,
      p_role_id: userData.role_id,
    });

    if (roleError) throw roleError;

    const user = await this.getById(authData.user.id);
    if (!user) throw new Error('Failed to fetch created user');

    return user;
  },

  async update(userId: string, updates: UpdateUserData): Promise<UserWithProfile> {
    if (updates.role_id) {
      const { error: roleError } = await supabase.rpc('assign_user_role', {
        p_user_id: userId,
        p_role_id: updates.role_id,
      });

      if (roleError) throw roleError;
    }

    const { error: profileError } = await supabase.rpc('update_user_profile', {
      p_user_id: userId,
      p_full_name: updates.full_name || null,
      p_phone: updates.phone || null,
      p_designation: updates.designation || null,
      p_blood_group: updates.blood_group || null,
      p_gender: updates.gender || null,
      p_address: updates.address || null,
      p_signature_url: updates.signature_url || null,
      p_status: updates.status || null,
    });

    if (profileError) throw profileError;

    const user = await this.getById(userId);
    if (!user) throw new Error('Failed to fetch updated user');

    return user;
  },

  async delete(userId: string): Promise<void> {
    const { error } = await supabase.auth.admin.deleteUser(userId);
    if (error) throw error;
  },

  async updatePassword(userId: string, newPassword: string): Promise<void> {
    const { error } = await supabase.auth.admin.updateUserById(userId, {
      password: newPassword,
    });

    if (error) throw error;
  },

  async uploadSignature(userId: string, file: File): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = `signatures/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('signatures')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage
      .from('signatures')
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  },
};

export const permissionsService = {
  async getUserPermissions(userId: string): Promise<string[]> {
    const { data, error } = await supabase.rpc('get_user_permissions', {
      p_user_id: userId,
    });

    if (error) {
      console.error('Error fetching user permissions:', error);
      return [];
    }

    return data || [];
  },

  async hasPermission(userId: string, permission: string): Promise<boolean> {
    const { data, error } = await supabase.rpc('has_permission', {
      p_user_id: userId,
      p_permission: permission,
    });

    if (error) {
      console.error('Error checking permission:', error);
      return false;
    }

    return data === true;
  },

  async getUserRole(userId: string): Promise<string | null> {
    const { data, error } = await supabase.rpc('get_user_role', {
      p_user_id: userId,
    });

    if (error) {
      console.error('Error fetching user role:', error);
      return null;
    }

    return data;
  },
};
